import * as React from 'react';
import { createContext, useContext, useRef, useState } from 'react';

import type { AiCoach_RespondToUserMessageInput as AiCoachRespondToUserMessageInput } from '__generated__/graphql-types';

import type { ChatMessageProps } from 'bundles/ai-coach-platform/components/patterns/chat/common';

export type CoachChatConfig = Omit<AiCoachRespondToUserMessageInput, 'message'> & { message?: undefined };

type CoachStateProviderType = {
  coachOpen: boolean;
  setCoachOpen: React.Dispatch<React.SetStateAction<boolean>>;
  contextConfig: CoachChatConfig;
  setContextConfig: React.Dispatch<React.SetStateAction<CoachChatConfig>>;
  renderCustomMessage?: (props: ChatMessageProps) => React.ReactElement | null;
  setRenderCustomMessage: React.Dispatch<
    React.SetStateAction<((props: ChatMessageProps) => React.ReactElement | null) | undefined>
  >;
  triggerElementRef: React.MutableRefObject<HTMLElement | null>;
  setTriggerElementRef: (elm: HTMLElement | null) => void;
  isContextConfigLoading?: boolean;
};
const defaultCoachState: CoachStateProviderType = {
  coachOpen: false,
  setCoachOpen: () => {},
  contextConfig: {
    config: {
      coachActionId: '',
      coachModeId: 'BASIC',
    },
  },
  setContextConfig: () => {},
  renderCustomMessage: undefined,
  setRenderCustomMessage: () => {},
  triggerElementRef: { current: null },
  setTriggerElementRef: () => {},
};

const CoachStateContext = createContext(defaultCoachState);

/**
 * @description This hook is used to access the coach state such as open/close state and context config.
 */
export const useCoachState = () => {
  const context = useContext(CoachStateContext);
  return context;
};

/**
 * @description This provider is used to manage the state of Coach.
 */
export const CoachStateProvider = ({
  children,
  coachOpen: coachOpenProp,
  isContextConfigLoading,
}: {
  children: React.ReactNode;
  coachOpen?: boolean;
  isContextConfigLoading?: boolean;
}) => {
  const [coachOpen, setCoachOpen] = useState(coachOpenProp ?? defaultCoachState.coachOpen);
  const [contextConfig, setContextConfig] = useState<CoachChatConfig>(defaultCoachState.contextConfig);
  const [renderCustomMessage, setRenderCustomMessage] = useState<
    ((props: ChatMessageProps) => React.ReactElement | null) | undefined
  >(defaultCoachState.renderCustomMessage);
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const setTriggerElementRef = (elm: HTMLElement | null) => {
    triggerElementRef.current = elm;
  };

  return (
    <CoachStateContext.Provider
      value={{
        coachOpen,
        setCoachOpen,
        isContextConfigLoading,
        contextConfig,
        setContextConfig: (...data) => {
          setContextConfig(...data);
        },
        renderCustomMessage,
        setRenderCustomMessage,
        triggerElementRef,
        setTriggerElementRef,
      }}
    >
      {children}
    </CoachStateContext.Provider>
  );
};
